import { NavigateFunction } from 'react-router-dom';

interface NavigationHistory {
  path: string;
  timestamp: number;
  searchParams?: string;
}

class NavigationService {
  private history: NavigationHistory[] = [];
  private maxHistorySize = 10;
  private currentNavigate: NavigateFunction | null = null;

  /**
   * Set the navigate function from React Router
   */
  setNavigate(navigate: NavigateFunction) {
    this.currentNavigate = navigate;
  }

  /**
   * Add a page to navigation history
   */
  addToHistory(path: string, searchParams?: string) {
    const historyItem: NavigationHistory = {
      path,
      timestamp: Date.now(),
      searchParams
    };

    // Create full path for comparison
    const fullPath = searchParams ? `${path}${searchParams}` : path;
    
    // Remove duplicate entries for the same full path
    this.history = this.history.filter(item => {
      const itemFullPath = item.searchParams ? `${item.path}${item.searchParams}` : item.path;
      return itemFullPath !== fullPath;
    });
    
    // Add new entry
    this.history.push(historyItem);

    // Keep only the last maxHistorySize entries
    if (this.history.length > this.maxHistorySize) {
      this.history = this.history.slice(-this.maxHistorySize);
    }

  }

  /**
   * Get the previous page from history
   */
  getPreviousPage(): string | null {
    if (this.history.length < 2) {
      return null;
    }
    
    // Get current page and previous page
    const currentPage = this.history[this.history.length - 1];
    const previousPage = this.history[this.history.length - 2];
    
    const currentFullPath = currentPage.searchParams 
      ? `${currentPage.path}${currentPage.searchParams}`
      : currentPage.path;
    const previousFullPath = previousPage.searchParams 
      ? `${previousPage.path}${previousPage.searchParams}`
      : previousPage.path;
    
    // If current page and previous page are the same, try to find a different page
    if (currentFullPath === previousFullPath) {
      // Look for a page that's different from current page
      for (let i = this.history.length - 3; i >= 0; i--) {
        const page = this.history[i];
        const pageFullPath = page.searchParams 
          ? `${page.path}${page.searchParams}`
          : page.path;
        
        if (pageFullPath !== currentFullPath) {
          return pageFullPath;
        }
      }
      
      // If no different page found, return null
      return null;
    }
    
    return previousFullPath;
  }

  /**
   * Navigate back to the previous page
   */
  goBack(): boolean {
    const previousPage = this.getPreviousPage();
    
    if (previousPage && this.currentNavigate) {
      // Remove the current page from history before navigating
      // This prevents infinite loops
      if (this.history.length > 0) {
        this.history.pop();
      }
      
      this.currentNavigate(previousPage);
      return true;
    }
    
    return false;
  }

  /**
   * Navigate back to the previous page when API call fails
   */
  handleApiFailure(): boolean {
    // Check if current page is the same as previous page (infinite loop prevention)
    if (this.isCurrentPageSameAsPrevious()) {
      // Clear history and go to home page
      this.history = [];
      if (this.currentNavigate) {
        this.currentNavigate('/');
        return true;
      }
      return false;
    }
    
    // Check if we have enough history to go back
    if (this.history.length < 2) {
      if (this.currentNavigate) {
        this.currentNavigate('/');
        return true;
      }
      return false;
    }
    
    const success = this.goBack();
    
    if (!success) {
      // Fallback to home page if no history
      if (this.currentNavigate) {
        this.currentNavigate('/');
      }
    }
    
    return success;
  }

  /**
   * Clear navigation history
   */
  clearHistory() {
    this.history = [];
    console.log('Navigation history cleared');
  }

  /**
   * Get current navigation history
   */
  getHistory(): NavigationHistory[] {
    return [...this.history];
  }

  /**
   * Check if there's a previous page available
   */
  hasPreviousPage(): boolean {
    return this.history.length >= 2;
  }


  /**
   * Check if current page is the same as previous page
   */
  isCurrentPageSameAsPrevious(): boolean {
    if (this.history.length < 2) {
      return false;
    }
    
    const currentPage = this.history[this.history.length - 1];
    const previousPage = this.history[this.history.length - 2];
    
    const currentFullPath = currentPage.searchParams 
      ? `${currentPage.path}${currentPage.searchParams}`
      : currentPage.path;
    const previousFullPath = previousPage.searchParams 
      ? `${previousPage.path}${previousPage.searchParams}`
      : previousPage.path;
    
    return currentFullPath === previousFullPath;
  }
}

// Create singleton instance
export const navigationService = new NavigationService();

// Export types
export type { NavigationHistory };
